import { CEFR_LEVELS, type CefrLevel } from "@rmnlng/shared";
import { ArrowLeft, BookmarkCheck, Check, X } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useExamAttempt, type ExamAttemptDetailAnswer } from "@/lib/exam";
import { useT, type I18nKey } from "@/lib/i18n";
import { cn } from "@/lib/utils";

// Question shapes — the post-submit payload includes the answer keys
// (correctAnswer / acceptedAnswers) so the Review screen can render
// "what you typed" alongside "what was correct". Discriminated by
// section, narrowed in the row component.
type QuestionUnion = {
  // Vocab MCQ + grammar MCQ
  correctAnswer?: string;
  options?: string[];
  // Vocab type + grammar fill + grammar transform
  acceptedAnswers?: string[];
  // Vocab prompt
  lemma?: string;
  language?: "RO" | "RU";
  // Grammar prompt
  sentence?: string;
  translation?: string;
  hints?: string[];
  // Transform-only source pair
  sourceSentence?: string;
  sourceTranslation?: string;
};

// `/levels/:level/exam/result/:attemptId/review`
//
// Shows every question of a submitted attempt with the user's answer,
// the correct answer, and a green/red status mark. Powered by
// `GET /levels/:level/exam/attempt/:attemptId`. The CTA from the
// Results screen routes here; back arrow returns to Levels.
export function ExamReviewPage() {
  const { level, attemptId } = useParams<{ level: string; attemptId: string }>();
  const t = useT();

  const isValid = CEFR_LEVELS.includes(level as CefrLevel);
  const targetLevel = level as CefrLevel;
  const { data, isPending, isError } = useExamAttempt(targetLevel, attemptId);

  if (!isValid) return <Navigate to="/levels" replace />;

  return (
    <div className="flex min-h-[calc(100dvh-40px)] flex-col gap-3.5">
      <Header level={targetLevel} totalCorrect={data?.attempt.score ?? null} />

      {isPending ? (
        <ListSkeleton />
      ) : isError || !data ? (
        <div className="rounded-[var(--r-md)] bg-[color:var(--again-soft)] px-3.5 py-3 text-[12px] text-[color:var(--again)]">
          {t("exam.review_error")}
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {data.answers.map((row) => (
            <AnswerRow key={row.id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}

function Header({ level, totalCorrect }: { level: CefrLevel; totalCorrect: number | null }) {
  const t = useT();
  return (
    <div className="-mx-4 border-b border-border">
      <div className="flex items-center gap-2 px-3 pt-1 pb-2">
        <Link
          to={`/levels/${level}`}
          aria-label={t("common.back")}
          className="-ml-2 flex size-9 items-center justify-center rounded-full text-text-2 transition-colors hover:text-text"
        >
          <ArrowLeft className="size-5" />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold leading-[1.2]">{t("exam.review_title")}</div>
          <div className="mt-0.5 font-mono text-[10px] text-text-3">
            {totalCorrect !== null ? t("exam.review_subtitle", { pct: totalCorrect }) : ""}
          </div>
        </div>
      </div>
    </div>
  );
}

// One row per question. Compact: section eyebrow + prompt + your
// answer + correct answer + ✓/✗ badge. Clickable would be useful
// later (deep link to the lemma / rule), but for v1 it's a static
// summary.
function AnswerRow({ row }: { row: ExamAttemptDetailAnswer }) {
  const t = useT();
  const q = row.question as QuestionUnion;
  const correctAnswer = q.correctAnswer ?? q.acceptedAnswers?.[0] ?? "";

  // Prompt depends on section. For the vocab sections we render the
  // lemma + a small direction tag; for grammar sections we render the
  // sentence (with `___` blank visible).
  const isVocab = row.section === "vocab_mcq" || row.section === "vocab_type";
  const prompt = isVocab ? (q.lemma ?? "") : (q.sentence ?? "");

  return (
    <li className="rounded-[var(--r-md)] border border-border bg-surface p-3">
      <div className="flex items-start gap-3">
        <StatusBadge correct={row.correct} />
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-text-3">
              {row.position}. {t(sectionLabelKey(row.section))}
            </span>
            {row.flagged && <BookmarkCheck className="size-3 text-[color:var(--accent)]" aria-label={t("exam.flag")} />}
          </div>
          <div className={cn("text-[14px]", isVocab ? "font-semibold" : "leading-snug")}>{prompt}</div>
          {/* Your answer */}
          <div className="mt-1.5 flex flex-col gap-0.5 font-mono text-[11px] tabular-nums">
            <div className="text-text-3">
              <span>{t("exam.review_your_answer")}: </span>
              <span
                className={cn(
                  row.userAnswer ? "" : "italic",
                  row.correct ? "text-[color:var(--good)]" : "text-[color:var(--again)]",
                )}
              >
                {row.userAnswer || t("exam.review_blank")}
              </span>
            </div>
            {!row.correct && (
              <div className="text-text-3">
                <span>{t("exam.review_correct_answer")}: </span>
                <span className="text-[color:var(--good)]">{correctAnswer}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}

function StatusBadge({ correct }: { correct: boolean }) {
  return (
    <div
      className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full"
      style={{
        background: correct ? "var(--good)" : "var(--again)",
        color: "white",
      }}
      aria-label={correct ? "correct" : "incorrect"}
    >
      {correct ? <Check className="size-3" strokeWidth={3} /> : <X className="size-3" strokeWidth={3} />}
    </div>
  );
}

function sectionLabelKey(section: string): I18nKey {
  return `exam.section_${section}` as I18nKey;
}

function ListSkeleton() {
  return (
    <ul className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="rounded-[var(--r-md)] border border-border bg-surface p-3">
          <div className="flex items-start gap-3">
            <Skeleton className="mt-0.5 size-5 rounded-full" />
            <div className="flex-1">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-2 h-4 w-2/3" />
              <Skeleton className="mt-2 h-3 w-1/2" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
